// PostHog `before_send` hook. It has two jobs, both of them about keeping the
// production project readable.
//
// 1. Non-production origins must not file into the production project. The
//    project key is hardcoded in index.jsx and is the same one every
//    environment ships with, so a developer's `yarn start`, an ngrok tunnel, a
//    Coder relay session and the staging bucket all report as production
//    traffic. Roughly a third of the project's exception events over the last
//    30 days came from developer hosts, generated by five people.
//
//    Filtering on hostname rather than build mode is deliberate: there is one
//    `vite build` for every environment, so `import.meta.env.PROD` is true on
//    staging and on any production build a developer serves through a tunnel.
//    Only the hostname distinguishes them.
//
// 2. Exceptions that carry nothing we can act on are dropped everywhere,
//    including production: the benign ResizeObserver loop notice, the opaque
//    cross-origin "Script error.", and errors thrown entirely inside a vendor
//    script (reCAPTCHA, GTM tags, browser extensions).
//
// The long-term fix for (1) is to move the project key to `VITE_POSTHOG_KEY`
// sourced from the per-environment .env secret, exactly as `VITE_SENTRY_DSN`
// already is, and give staging its own project. That needs the secret to exist
// before the code ships, so it is a follow-up. This hook is the stopgap.

// Exact hostnames that are never production.
const NON_PRODUCTION_HOSTS = new Set([
  "localhost",
  "127.0.0.1",
  "[::1]",
  // Staging. Errors here are already covered by a separate Sentry project
  // (nbyula-discovery-client-staging), so nothing is lost by keeping its
  // product analytics out of the production project.
  "discovery.navtest.co.in",
]);

// Suffix-matched, so a production host that merely contains one of these words
// (say ngrok-docs.nbyula.com) is not caught.
const NON_PRODUCTION_HOST_SUFFIXES = [
  ".ngrok-free.app",
  ".ngrok.io",
  ".ngrok.app",
  ".ngrok-free.dev",
  // Personal dev relay, e.g. discovery--dev--<name>.code.relay.corp.nbyula.com
  ".code.relay.corp.nbyula.com",
];

export const isNonProductionHost = (hostname) => {
  const host = String(hostname ?? "").toLowerCase();
  if (!host) return false;
  if (NON_PRODUCTION_HOSTS.has(host)) return true;
  return NON_PRODUCTION_HOST_SUFFIXES.some((suffix) => host.endsWith(suffix));
};

// Chrome raises "ResizeObserver loop completed with undelivered notifications"
// (and the older "ResizeObserver loop limit exceeded") when resize callbacks do
// not settle inside one frame. It is a browser notice, not a failure: the page
// keeps working and the event carries no stack frames. PostHog's global error
// handler still captures it, so each notice opens a fresh error tracking issue.
const RESIZE_OBSERVER_LOOP_NOTICE = "ResizeObserver loop";

export const isResizeObserverLoopNotice = (event) => {
  if (event?.event !== "$exception") return false;
  const exceptions = event.properties?.$exception_list ?? [];
  return exceptions.some((exception) =>
    String(exception?.value ?? "").includes(RESIZE_OBSERVER_LOOP_NOTICE)
  );
};

const exceptionListOf = (event) =>
  event?.event === "$exception" ? event.properties?.$exception_list ?? [] : [];

const framesOf = (exception) => exception?.stacktrace?.frames ?? [];

// When a script from another origin throws and its tag is not loaded with
// `crossorigin` + CORS headers, the browser hides everything and window.onerror
// receives only the string "Script error." with no stack, file or line. It is
// one of the noisiest issues in the project (thousands of events a month) and
// can never be traced back to a cause, so it is dropped. A same-message event
// that does carry frames is kept: that is a real error that merely says this.
const OPAQUE_SCRIPT_ERROR = /^Script error\.?$/;

export const isOpaqueCrossOriginError = (event) => {
  const exceptions = exceptionListOf(event);
  return (
    exceptions.length > 0 &&
    exceptions.every(
      (exception) =>
        OPAQUE_SCRIPT_ERROR.test(String(exception?.value ?? "").trim()) &&
        framesOf(exception).length === 0
    )
  );
};

// Our bundle is served from the page origin (no Vite `base`), and every
// environment lives under one of these domains.
const FIRST_PARTY_DOMAINS = ["nbyula.com", "nbl.one"];

// A frame is third-party only when we can positively say so: its filename is
// an extension URL, or an http(s) URL on a host that is neither the page host
// nor one of ours. Frames with no filename, or "<anonymous>", stay unknown and
// therefore keep the event.
const isThirdPartyFrame = (frame, pageHost) => {
  const filename = String(frame?.filename ?? frame?.abs_path ?? "");
  if (/^(chrome|moz|safari(-web)?)-extension:\/\//.test(filename)) return true;
  let host;
  try {
    const url = new URL(filename);
    if (url.protocol !== "http:" && url.protocol !== "https:") return false;
    host = url.hostname.toLowerCase();
  } catch {
    return false;
  }
  if (host === pageHost) return false;
  return !FIRST_PARTY_DOMAINS.some(
    (domain) => host === domain || host.endsWith(`.${domain}`)
  );
};

/**
 * True when every frame of every exception in the event sits in a vendor
 * script, e.g. the `TypeError ... reading 'logging'` raised inside Google's
 * recaptcha__en.js. Nothing in our code can fix those, so they are noise. One
 * first-party or unknown frame anywhere is enough to keep the event.
 */
export const isThirdPartyOnlyException = (event, pageHost) => {
  const exceptions = exceptionListOf(event);
  const host = String(pageHost ?? "").toLowerCase();
  return (
    exceptions.length > 0 &&
    exceptions.every((exception) => {
      const frames = framesOf(exception);
      return (
        frames.length > 0 &&
        frames.every((frame) => isThirdPartyFrame(frame, host))
      );
    })
  );
};

/**
 * Build the `before_send` hook for a given origin.
 *
 * Taking the hostname as an argument rather than reading `window.location`
 * inside the hook keeps this module pure and directly testable, and means the
 * host is resolved once at init instead of on every event.
 *
 * @param {string} hostname typically `window.location.hostname`
 * @returns {(event: object|null) => object|null} the event to send, or null to drop it
 */
export const createPosthogBeforeSend = (hostname) => {
  if (isNonProductionHost(hostname)) {
    // Nothing from this origin belongs in the production project.
    return () => null;
  }
  return (event) =>
    isResizeObserverLoopNotice(event) ||
    isOpaqueCrossOriginError(event) ||
    isThirdPartyOnlyException(event, hostname)
      ? null
      : event;
};
